/**
 * SEO client-side — config pública + head-manager imperativo.
 *
 * El backend (SeoController) expone GET /seo/config con los metadatos/campañas
 * que el Super Admin configura. Acá:
 *   - `usePublicSeo()` la trae con TanStack Query (cache larga, sin auth).
 *   - un mini head-manager idempotente setea <title>/<meta>/<link>/<script ld>
 *     en document.head sin dependencias (react-helmet no está instalado).
 *
 * El head-manager es "upsert": busca el nodo por un selector estable, lo crea si
 * falta y actualiza su contenido; si el valor es vacío, lo elimina. Así cada
 * página puede pedir su título/descripcion sin duplicar tags.
 */
import { useQuery } from '@tanstack/react-query';

import { api } from './api';

/** Bloque de metadatos por idioma. */
export interface SeoLangDefaults {
  title: string;
  description: string;
  keywords: string;
}

/** Shape EXACTO de GET /seo/config (contrato con el backend). */
export interface PublicSeoConfig {
  site_name: string;
  canonical_base: string;
  og_image: string;
  twitter_handle: string;
  robots_index: boolean;
  defaults: {
    es: SeoLangDefaults;
    pt: SeoLangDefaults;
  };
  verification: {
    google: string;
    bing: string;
  };
  analytics: {
    ga4_id: string;
    gtm_id: string;
    meta_pixel_id: string;
    google_ads_id: string;
    google_ads_label: string;
  };
}

const SEO_CONFIG_KEY = ['seo', 'config'] as const;

/**
 * Trae la config pública de SEO/SEM. `skipAuthRefresh` porque es un endpoint
 * público (un 401 no debe disparar el ciclo de refresh). Cache larga: cambia
 * poco y no queremos recargarla en cada navegación.
 */
export function usePublicSeo() {
  return useQuery({
    queryKey: SEO_CONFIG_KEY,
    queryFn: () => api.get<PublicSeoConfig>('/seo/config', { skipAuthRefresh: true }),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
  });
}

// --- Head-manager imperativo (idempotente) ----------------------------------

const isBrowser = typeof document !== 'undefined';

/** Setea el <title> del documento. Vacío => no toca (deja el actual). */
export function setTitle(title: string): void {
  if (!isBrowser || !title) return;
  document.title = title;
}

/**
 * Upsert de un <meta>. `attr` = 'name' | 'property' (og usa property). Content
 * vacío => elimina el tag (si existía). Marca los nodos que gestiona con
 * data-seo para no pisar metas estáticas ajenas.
 */
export function setMeta(attr: 'name' | 'property', key: string, content: string): void {
  if (!isBrowser) return;
  const selector = `meta[${attr}="${cssEscape(key)}"]`;
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!content) {
    // Sólo removemos los que gestionamos nosotros (evita borrar metas estáticas
    // que quizás algún crawler ya leyó del index.html).
    if (el && el.dataset.seo === '1') el.remove();
    return;
  }
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    el.dataset.seo = '1';
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

/** Upsert de un <link>. `href` vacío => elimina el link gestionado. */
export function setLink(rel: string, href: string, hreflang?: string): void {
  if (!isBrowser) return;
  const relSel = `link[rel="${cssEscape(rel)}"]` + (hreflang ? `[hreflang="${cssEscape(hreflang)}"]` : '');
  let el = document.head.querySelector<HTMLLinkElement>(relSel);
  if (!href) {
    if (el && el.dataset.seo === '1') el.remove();
    return;
  }
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    if (hreflang) el.setAttribute('hreflang', hreflang);
    el.dataset.seo = '1';
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

/** Atajo para el <link rel="canonical">. */
export function setCanonical(href: string): void {
  setLink('canonical', href);
}

/**
 * Upsert de un bloque JSON-LD identificado por `id` (data-seo-ld). Objeto vacío
 * / null => elimina el script.
 */
export function setJsonLd(id: string, obj: unknown | null): void {
  if (!isBrowser) return;
  const selector = `script[type="application/ld+json"][data-seo-ld="${cssEscape(id)}"]`;
  let el = document.head.querySelector<HTMLScriptElement>(selector);
  if (!obj) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.dataset.seoLd = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(obj);
}

/** Escape mínimo para usar un valor dentro de un selector de atributo. */
function cssEscape(value: string): string {
  return value.replace(/["\\]/g, '\\$&');
}
